import {publishOptimisticFields} from '@github-ui/optimistic-connection/connectionMutationBridge'
import {issuePath} from '@github-ui/paths'
import {useMutation} from '@github-ui/react-query'
import {deleteIssueFieldValueMutation as deleteIssueFieldValueQueryId} from '@github-ui/server-persisted-queries'
import type {
  DeleteIssueFieldValueInput,
  DeleteIssueFieldValueMutationMutation,
} from '@github-ui/server-persisted-queries/types/deleteIssueFieldValueMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'
import {useRelayEnvironment} from 'react-relay'
import type {Environment} from 'relay-runtime'

import {removeIssueFieldValue} from './helpers'
import {
  applyOptimisticIssueFieldValueRemovalToRelay,
  commitIssueFieldValueRemovalToRelay,
} from './issue-field-value-relay-bridge'

const DELETE_ERROR = 'Delete issue field value failed'

type DeleteIssueFieldValueMutationInput = {
  issueId: Extract<DeleteIssueFieldValueInput['issueId'], string>
  fieldId: Extract<DeleteIssueFieldValueInput['fieldId'], string>
  owner: string
  repo: string
  number: number
}

type DeleteIssueFieldValueMutationOptions = {
  input: DeleteIssueFieldValueMutationInput
  fieldName?: string
  onCompleted?: () => void
  onError?: (error?: Error) => void
}

type DeleteResponse = {data?: DeleteIssueFieldValueMutationMutation | null; errors?: readonly unknown[] | null}

export async function commitDeleteIssueFieldValueMutation({
  environment,
  input,
  fieldName,
  onCompleted,
  onError,
}: {
  environment: Environment
} & DeleteIssueFieldValueMutationOptions) {
  const operation = fieldName ? removeIssueFieldValue(input.issueId, input.fieldId, fieldName) : undefined
  const publication = operation ? publishOptimisticFields() : undefined
  const optimisticRelayUpdate = applyOptimisticIssueFieldValueRemovalToRelay(environment, input.issueId, input.fieldId)

  try {
    await deleteIssueFieldValueMutation(input)
    commitIssueFieldValueRemovalToRelay(environment, input.issueId, input.fieldId)
    if (operation && publication) {
      publication.completed(operation)
    }
    onCompleted?.()
  } catch (error) {
    const surfacedError = error instanceof Error ? error : new Error(DELETE_ERROR)
    if (operation && publication) {
      publication.failed(surfacedError)
    }
    onError?.(surfacedError)
  } finally {
    optimisticRelayUpdate.dispose()
  }
}

export function useDeleteIssueFieldValueMutation() {
  const environment = useRelayEnvironment()
  const {mutate} = useMutation({
    mutationKey: ['issue-metadata', 'delete-issue-field-value'],
    mutationFn: async (options: DeleteIssueFieldValueMutationOptions) => {
      await commitDeleteIssueFieldValueMutation({environment, ...options})
    },
    onError: (error, options) => {
      options.onError?.(error instanceof Error ? error : new Error(DELETE_ERROR))
    },
  })

  return useCallback((options: DeleteIssueFieldValueMutationOptions) => mutate(options), [mutate])
}

async function deleteIssueFieldValueMutation(input: DeleteIssueFieldValueMutationInput): Promise<void> {
  const query = new URLSearchParams({queryId: deleteIssueFieldValueQueryId})
  const path = `${issuePath({
    owner: input.owner,
    repo: input.repo,
    issueNumber: input.number,
  })}/field_value.json`
  const response = await reactFetchJSON(`${path}?${query.toString()}`, {
    method: 'DELETE',
    body: {fieldId: input.fieldId},
  })
  if (!response.ok) throw new Error(`${DELETE_ERROR} (${response.status})`)

  let json: DeleteResponse
  try {
    json = (await response.json()) as DeleteResponse
  } catch {
    throw new Error(`${DELETE_ERROR}: response was invalid`)
  }

  const message = json.errors?.length
    ? json.errors
        .flatMap(error =>
          error && typeof error === 'object' && 'message' in error && typeof error.message === 'string'
            ? [error.message]
            : [],
        )
        .join(', ')
    : undefined
  if (message || !json.data) throw new Error(message || `${DELETE_ERROR}: deleteIssueFieldValue data was missing`)

  const payload = json.data.deleteIssueFieldValue
  if (!payload) throw new Error(`${DELETE_ERROR}: payload was missing`)
  if (!payload.issue) throw new Error(`${DELETE_ERROR}: issue was missing`)
  if (payload.issue.id !== input.issueId) throw new Error(`${DELETE_ERROR}: issue did not match`)
  if (!payload.success) throw new Error(`${DELETE_ERROR}: delete did not succeed`)
}
